import { NotificationSoundKey, NotificationSoundOption } from './types'

interface SoundTone {
  at: number
  frequency: number
  duration: number
  volume: number
  wave?: OscillatorType
  endFrequency?: number
}

export interface GeneratedNotificationSound extends NotificationSoundOption {
  tones: SoundTone[]
  gain: number
}

export interface AudioNotificationSound extends NotificationSoundOption {
  audioUrl: string
  duration: number
  gain: number
}

export type NotificationSound = GeneratedNotificationSound | AudioNotificationSound

export const DEFAULT_NOTIFICATION_SOUND_KEY: NotificationSoundKey = 'crystal'

export const NOTIFICATION_SOUNDS: NotificationSound[] = [
  {
    key: 'crystal',
    label: 'Кристалл',
    description: 'По умолчанию',
    gain: 4.4,
    tones: [
      { frequency: 523, at: 0, duration: 0.34, volume: 0.1 },
      { frequency: 784, at: 0, duration: 0.24, volume: 0.07 },
      { frequency: 1046, at: 0.045, duration: 0.18, volume: 0.04 },
    ],
  },
  {
    key: 'double_rise',
    label: 'Двойной подъём',
    description: 'Два коротких сигнала',
    audioUrl: '/sounds/notifications/custom/notice-14.wav',
    duration: 0.548571,
    gain: 2.4,
  },
  {
    key: 'copper_response',
    label: 'Медный отклик',
    description: 'Металлический отклик',
    audioUrl: '/sounds/notifications/custom/sound-11295.wav',
    duration: 1.149388,
    gain: 1,
  },
  {
    key: 'bright_chime',
    label: 'Яркий перезвон',
    description: 'Продолжительный перезвон',
    audioUrl: '/sounds/notifications/custom/ringing-short.wav',
    duration: 2.0361,
    gain: 2.1,
  },
  {
    key: 'silver_bell',
    label: 'Серебряный звон',
    description: 'Лёгкий двойной звон',
    gain: 4.9,
    tones: [
      { frequency: 587, at: 0, duration: 0.13, volume: 0.14, wave: 'triangle' },
      { frequency: 880, at: 0.19, duration: 0.24, volume: 0.1 },
      { frequency: 1174, at: 0.21, duration: 0.14, volume: 0.035 },
    ],
  },
  {
    key: 'clear_line',
    label: 'Чистая линия',
    description: 'Восходящий сигнал',
    gain: 3.3,
    tones: [
      {
        frequency: 440,
        endFrequency: 660,
        at: 0,
        duration: 0.36,
        volume: 0.17,
      },
    ],
  },
  {
    key: 'air_chime',
    label: 'Воздушный перезвон',
    description: 'Мягкий аккорд',
    gain: 4.5,
    tones: [
      { frequency: 440, at: 0, duration: 0.4, volume: 0.075 },
      { frequency: 554, at: 0.035, duration: 0.34, volume: 0.07 },
      { frequency: 659, at: 0.075, duration: 0.29, volume: 0.065 },
      { frequency: 880, at: 0.17, duration: 0.15, volume: 0.035 },
    ],
  },
  {
    key: 'muted_bell',
    label: 'Приглушённый колокол',
    description: 'Низкий спокойный',
    gain: 3.2,
    tones: [
      { frequency: 165, at: 0, duration: 0.46, volume: 0.12 },
      { frequency: 330, at: 0, duration: 0.46, volume: 0.12 },
    ],
  },
  {
    key: 'deep_drop',
    label: 'Глубокая капля',
    description: 'Короткий нисходящий',
    gain: 2.4,
    tones: [
      {
        frequency: 260,
        endFrequency: 151,
        at: 0,
        duration: 0.34,
        volume: 0.24,
      },
    ],
  },
  {
    key: 'calm_beacon',
    label: 'Спокойный маяк',
    description: 'Текущий звук',
    gain: 2,
    tones: [
      { frequency: 262, at: 0, duration: 0.13, volume: 0.34 },
      { frequency: 392, at: 0.13, duration: 0.2, volume: 0.26 },
    ],
  },
]

const soundByKey = new Map(NOTIFICATION_SOUNDS.map(sound => [sound.key, sound]))

export const getNotificationSound = (key: NotificationSoundKey): NotificationSound =>
  soundByKey.get(key) || soundByKey.get(DEFAULT_NOTIFICATION_SOUND_KEY)!

export const isAudioNotificationSound = (
  sound: NotificationSound,
): sound is AudioNotificationSound => 'audioUrl' in sound
